import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ActionsMenu from '../../../../components/ui/ActionsMenu';
import { EditOutlined, SolutionOutlined } from '@ant-design/icons';
import React from 'react';
import { useFetchData, useFetchFile } from '../../../../hooks';
import { PurchaseRequestRecord } from '../../../../types/PurchaseRequest';
import { MenuProps, message } from 'antd';
import PurchaseRequestLoadingView from './PurchaseRequestLoadingView';
import PageHeaderAction from '../../../../components/PageHeaderAction';
import AuditCard from '../../../../components/AuditCard';
import TrackingChain from '../../../../components/TrackingChain';
import PurchaseRequestInfoStatus from './PurchaseRequestInfoStatus';
import PurchaseRequestInfoContent from './PurchaseRequestInfoContent';
import PrimaryButton from '../../../../components/ui/PrimaryButton';
import { StatusHistory } from '../../../../types';
import { RequestStatusEnum } from '../../../../lib/enums';
import { isUserCanCreate, isUserCanEdit } from '../../../../lib/utils';
import { useCanvassFromRequest } from '../../../../hooks/useCanvassHooks';
import { useAuthStore } from '../../../../store';

const PurchaseRequestInfoPage = () => {
  const api = '/pr/requests';
  const { user } = useAuthStore();
  const { mutate: startCanvass } = useCanvassFromRequest();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useFetchData<PurchaseRequestRecord>(
    `${api}/${params.id}`,
  );

  const { data: files } = useFetchFile(
    `${api}/${params.id}/files`,
    'api/pr/requests',
  );

  const { data: statusHistory, isLoading: isLoadingHistory } = useFetchData<
    StatusHistory[]
  >(`${api}/${params?.id}/status-logs`);

  const handleClose = () => {
    navigate('/purchase-request');
  };

  if (isLoading && isLoadingHistory) {
    return <PurchaseRequestLoadingView active={true} />;
  }

  const isCurrentUserCanEdit = () => {
    return (
      user?.role === 'ROLE_ADMIN' ||
      isUserCanEdit(user?.moduleAccess, 'Purchase Request')
    );
  };

  // Standard record actions. Edit is hidden when the user lacks write access
  // and disabled once the request is approved, matching the old button's rules.
  const actionItems: MenuProps['items'] = [
    ...(isCurrentUserCanEdit()
      ? [
          {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
            disabled: data?.status === RequestStatusEnum.APPROVED,
          },
        ]
      : []),
    // Canvassing covers the whole request: the same suppliers are asked for the
    // entire list at once, so this belongs here rather than on each line.
    ...(isUserCanCreate(user?.moduleAccess, 'Canvass')
      ? [
          {
            key: 'canvass',
            label: 'Canvass this request',
            icon: <SolutionOutlined />,
          },
        ]
      : []),
  ];

  const onActionClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'edit') navigate(`/purchase-request/request/${data?.id}`);
    if (key === 'canvass' && data?.id) {
      startCanvass(data.id, {
        onSuccess: (canvass: { id?: string; code?: string }) => {
          message.success(`Canvass ${canvass?.code ?? ''} opened`);
          navigate(`/purchase-canvass/${canvass?.id}`);
        },
        onError: (e: any) =>
          message.error(
            e?.response?.data?.message ??
              'Could not open a canvass for this request.',
          ),
      });
    }
  };

  return (
    <>
      {data !== null && data !== undefined && (
        <>
          <div className={'flex items-center justify-between'}>
            <PageHeaderAction
              moduleName={'Purchase Request'}
              action={searchParams.get('action')}
            />
            <ActionsMenu
              items={actionItems}
              onClick={onActionClick}
              onBack={handleClose}
            />
          </div>
          <div className={'flex'}>
            <PurchaseRequestInfoContent
              data={data}
              attachments={files}
              api={api}
              recordId={params.id}
              canEdit={isUserCanEdit(user?.moduleAccess, 'Purchase Request')}
            />
            <div className={'w-3/12'}>
              <PurchaseRequestInfoStatus
                data={data}
                statusHistory={statusHistory !== undefined ? statusHistory : []}
              />
              <div className={'m-4 space-y-4'}>
                <TrackingChain api={api} id={params.id} />
                <AuditCard
                  createdBy={data?.createdBy}
                  updatedBy={data?.updatedBy}
                  timeCreated={data?.timeCreated}
                  timeUpdated={data?.timeUpdated}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/*<div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>*/}
      {/*  <PurchaseOrderPrintTemplate*/}
      {/*    data={purchaseOrderInfo!}*/}
      {/*    ref={componentRef}*/}
      {/*  />*/}
      {/*</div>*/}
    </>
  );
};

export default PurchaseRequestInfoPage;
